<template>
  <MainLayout :initialDrawerExpanded="false" contentClass="pa-md-4" :has-sidebar="false">
    <template #header>
      <div class="overline">
        <PageBack
          v-if="team"
          :to="{ name: 'admin_organisation_detail', params: { id: team.organisation.id } }"
          :label="team.organisation.name"
        />
      </div>
    </template>

    <template #content>
      <v-container fluid class="pa-0 pa-md-4">
        <v-row>
          <v-col cols="12" md="10" offset-md="1">

            <div v-if="loading" class="d-flex justify-center py-12">
              <v-progress-circular indeterminate color="primary" />
            </div>

            <v-alert v-else-if="error" type="error" class="mb-4">{{ error }}</v-alert>

            <template v-else-if="team">

              <!-- Header -->
              <div class="d-flex align-center flex-wrap mb-5" style="gap: 12px">
                <v-avatar color="primary lighten-4" size="48">
                  <v-icon color="primary">mdi-account-group-outline</v-icon>
                </v-avatar>
                <div>
                  <div class="subtitle-1 font-weight-medium">{{ team.name }}</div>
                  <div class="caption grey--text">{{ team.organisation.name }}</div>
                </div>
              </div>

              <!-- Members table -->
              <v-card elevation="0" outlined>
                <v-card-title class="subtitle-1 font-weight-medium pb-1">
                  <v-icon left small>mdi-account-multiple-outline</v-icon>
                  Membres
                  <v-spacer />
                  <v-btn small elevation="0" color="primary" @click="openAddMembers">
                    <v-icon left small>mdi-account-plus-outline</v-icon>
                    Ajouter des membres
                  </v-btn>
                </v-card-title>

                <v-slide-y-transition>
                  <div v-if="selected.length" class="d-flex align-center px-4 py-2 grey lighten-4" style="gap: 8px">
                    <span class="caption grey--text">{{ selected.length }} sélectionné(s)</span>
                    <v-spacer />
                    <v-btn small text @click="openRoleDialog">
                      <v-icon left small>mdi-shield-account-outline</v-icon>
                      Changer le rôle
                    </v-btn>
                    <v-btn small text color="error" @click="removeDialog = true">
                      <v-icon left small>mdi-account-minus-outline</v-icon>
                      Retirer de l'équipe
                    </v-btn>
                  </div>
                </v-slide-y-transition>

                <v-data-table
                  v-model="selected"
                  :headers="memberHeaders"
                  :items="team.memberships"
                  item-key="id"
                  show-select
                  sort-by="username"
                  class="elevation-0"
                >
                  <template v-slot:[`item.role`]="{ item }">
                    <v-chip x-small :color="item.role === 'admin' ? 'primary' : ''" :dark="item.role === 'admin'">
                      {{ item.role === 'admin' ? 'Admin' : 'Membre' }}
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card>

            </template>

            <v-row v-else>
              <v-col cols="12" md="8" offset-md="2">
                <v-alert type="error" outlined>Équipe introuvable.</v-alert>
              </v-col>
            </v-row>

          </v-col>
        </v-row>
      </v-container>

      <!-- Add members dialog -->
      <v-dialog v-model="addDialog" max-width="480" @keydown.esc="addDialog = false">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-medium">Ajouter des membres</v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-autocomplete
              v-model="addForm.userIds"
              :items="availableUserChoices"
              multiple chips small-chips deletable-chips
              outlined dense
              label="Utilisateurs"
            />
            <v-select
              v-model="addForm.role"
              :items="roleChoices"
              label="Rôle"
              outlined dense
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="addDialog = false">Annuler</v-btn>
            <v-btn color="primary" elevation="0" :loading="addSaving" :disabled="!addForm.userIds.length" @click="submitAddMembers">
              Ajouter
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Change role dialog -->
      <v-dialog v-model="roleDialog" max-width="360" @keydown.esc="roleDialog = false">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-medium">Changer le rôle</v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-select
              v-model="roleForm.role"
              :items="roleChoices"
              label="Rôle"
              outlined dense
            />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="roleDialog = false">Annuler</v-btn>
            <v-btn color="primary" elevation="0" :loading="roleSaving" @click="submitSetRole">
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Remove members confirmation -->
      <v-dialog v-model="removeDialog" max-width="400">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-medium">Retirer de l'équipe</v-card-title>
          <v-card-text>
            Retirer {{ selected.length }} membre(s) de {{ team && team.name }} ? Les comptes utilisateurs ne sont pas supprimés.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="removeDialog = false">Annuler</v-btn>
            <v-btn color="error" elevation="0" :loading="removeSaving" @click="submitRemoveMembers">Retirer</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </MainLayout>
</template>

<script>
import PageBack from '@/components/ui/PageBack.vue'
import MainLayout from '@/layout/MainLayout.vue'
import {
  fetchAdminTeam,
  bulkAddTeamMembers,
  bulkSetTeamMemberRole,
  bulkRemoveTeamMembers,
  fetchAdminUsers,
} from '@/services/adminApi'

const ROLE_CHOICES = [
  { text: 'Membre', value: 'member' },
  { text: 'Admin', value: 'admin' },
]

export default {
  name: 'AdminTeamDetailView',
  components: { PageBack, MainLayout },

  data() {
    return {
      loading: false,
      error: null,
      team: null,
      selected: [],
      allUsers: [],

      addDialog: false,
      addForm: { userIds: [], role: 'member' },
      addSaving: false,

      roleDialog: false,
      roleForm: { role: 'member' },
      roleSaving: false,

      removeDialog: false,
      removeSaving: false,

      roleChoices: ROLE_CHOICES,
      memberHeaders: [
        { text: 'Utilisateur', value: 'username' },
        { text: 'Prénom', value: 'first_name' },
        { text: 'Nom', value: 'last_name' },
        { text: 'Email', value: 'email' },
        { text: 'Rôle', value: 'role' },
      ],
    }
  },

  computed: {
    teamId() {
      return this.$route.params.id
    },

    availableUserChoices() {
      const memberIds = new Set((this.team ? this.team.memberships : []).map(m => m.user_id))
      return this.allUsers
        .filter(u => !memberIds.has(u.id))
        .map(u => ({ text: u.username, value: u.id }))
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.team = await fetchAdminTeam(this.teamId)
        this.selected = []
      } catch {
        this.error = 'Impossible de charger cette équipe.'
      } finally {
        this.loading = false
      }
    },

    async openAddMembers() {
      this.addForm = { userIds: [], role: 'member' }
      if (!this.allUsers.length) {
        try {
          this.allUsers = await fetchAdminUsers()
        } catch {
          this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du chargement des utilisateurs.' })
          return
        }
      }
      this.addDialog = true
    },

    async submitAddMembers() {
      this.addSaving = true
      try {
        await bulkAddTeamMembers(this.teamId, { user_ids: this.addForm.userIds, role: this.addForm.role })
        this.addDialog = false
        await this.load()
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: "Erreur lors de l'ajout des membres." })
      } finally {
        this.addSaving = false
      }
    },

    openRoleDialog() {
      this.roleForm = { role: 'member' }
      this.roleDialog = true
    },

    async submitSetRole() {
      this.roleSaving = true
      try {
        await bulkSetTeamMemberRole(this.teamId, {
          membership_ids: this.selected.map(m => m.id),
          role: this.roleForm.role,
        })
        this.roleDialog = false
        await this.load()
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du changement de rôle.' })
      } finally {
        this.roleSaving = false
      }
    },

    async submitRemoveMembers() {
      this.removeSaving = true
      try {
        await bulkRemoveTeamMembers(this.teamId, { membership_ids: this.selected.map(m => m.id) })
        this.removeDialog = false
        await this.load()
      } catch {
        this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: 'Erreur lors du retrait des membres.' })
      } finally {
        this.removeSaving = false
      }
    },
  },
}
</script>
